import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { StepNotice } from '@partful/entities/instructions/stepNotice';
import { StepNoticeModel, StepNoticeType, StepNoticeWithContentModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Button } from '@partful/shared/ui/Button';
import { Input } from '@partful/shared/ui/Input';
import { Option, Select } from '@partful/shared/ui/Select';
import { Textarea } from '@partful/shared/ui/Textarea';
import { Typography } from '@partful/shared/ui/Typography';
import { useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './StepNoticeForm.module.scss';

interface StepNoticeFormProps {
  disabled?: boolean;
  initialValues?: StepNoticeModel & Partial<StepNoticeWithContentModel>;
  onCancelClick: () => void;
  onSubmit: SubmitHandler<StepNotice>;
}

export function StepNoticeForm({ disabled, initialValues, onCancelClick, onSubmit }: Readonly<StepNoticeFormProps>) {
  const { t: i18n } = useTranslation();

  const { formState, handleSubmit, control, watch } = useForm<StepNotice>({
    resolver: zodResolver(StepNotice),
    mode: 'all',
    values: {
      id: initialValues?.id,
      type: initialValues?.type ?? StepNoticeType.Info,
      title: initialValues?.content?.title ?? '',
      description: initialValues?.content?.description ?? '',
    },
  });

  const values = watch();

  const indicatorClassName = useMemo(() => {
    switch (values.type) {
      case StepNoticeType.Safety:
        return styles.safety;
      case StepNoticeType.Warning:
        return styles.warning;
      case StepNoticeType.Danger:
        return styles.danger;
      default:
        return styles.info;
    }
  }, [values.type]);

  return (
    <div className={classNames(styles.container, indicatorClassName)}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="notice-form"
        data-isvalid={formState.isValid}
        data-isdirty={formState.isDirty}
      >
        <div>
          <Typography as="label" fontWeight={600} htmlFor="type">
            {i18n('instructions:step.notice.type.label')}
          </Typography>
          <Controller
            name="type"
            control={control}
            render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
              <>
                <Select
                  className={styles.language}
                  size="small"
                  disabled={disabled}
                  id={name}
                  name={name}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  data-testid="notice-type-field"
                >
                  <Option value={StepNoticeType.Info} data-testid={`notice-type-${StepNoticeType.Info}-option`}>
                    {i18n('instructions:step.notice.type.options.info')}
                  </Option>
                  <Option value={StepNoticeType.Safety} data-testid={`notice-type-${StepNoticeType.Safety}-option`}>
                    {i18n('instructions:step.notice.type.options.safety')}
                  </Option>
                  <Option value={StepNoticeType.Warning} data-testid={`notice-type-${StepNoticeType.Warning}-option`}>
                    {i18n('instructions:step.notice.type.options.warn')}
                  </Option>
                  <Option value={StepNoticeType.Danger} data-testid={`notice-type-${StepNoticeType.Danger}-option`}>
                    {i18n('instructions:step.notice.type.options.danger')}
                  </Option>
                </Select>
                {
                  /* istanbul ignore next - options are default selected so validation should never trigger */
                  error && (
                    <Typography appearance="label" className={styles.errorMessage}>
                      <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                      {i18n(
                        /* istanbul ignore next - very defensive fallback case */
                        error.message ?? 'instructions:step.notice.type.validation.generic'
                      )}
                    </Typography>
                  )
                }
              </>
            )}
          />
        </div>
        <div>
          <Typography as="label" fontWeight={600} htmlFor="title">
            {i18n('instructions:step.notice.title.label')}
          </Typography>
          <Controller
            name="title"
            control={control}
            render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
              <>
                <Input
                  size="small"
                  disabled={disabled}
                  id={name}
                  name={name}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  data-testid="notice-title-field"
                />
                {error && (
                  <Typography appearance="label" className={styles.errorMessage} data-testid="notice-title-error">
                    <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                    {i18n(
                      /* istanbul ignore next - very defensive fallback case */
                      error.message ?? 'instructions:step.notice.title.validation.generic'
                    )}
                  </Typography>
                )}
              </>
            )}
          />
        </div>
        <div>
          <Typography as="label" fontWeight={600} htmlFor="description">
            {i18n('instructions:step.notice.description.label')}
          </Typography>
          <Controller
            name="description"
            control={control}
            render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
              <>
                <Textarea
                  size="small"
                  disabled={disabled}
                  minRows={3}
                  id={name}
                  name={name}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  data-testid="notice-description-field"
                />
                {error && (
                  <Typography appearance="label" className={styles.errorMessage} data-testid="notice-description-error">
                    <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                    {i18n(
                      /* istanbul ignore next - very defensive fallback case */
                      error.message ?? 'instructions:step.notice.description.validation.generic'
                    )}
                  </Typography>
                )}
              </>
            )}
          />
        </div>
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={!formState.isValid || !formState.isDirty || disabled}
            data-testid="save-notice-btn"
          >
            {i18n('instructions:stepBuilder.saveChanges')}
          </Button>
          <Button
            type="reset"
            variant="tertiary"
            onClick={onCancelClick}
            disabled={disabled}
            data-testid="cancel-notice-btn"
          >
            {i18n('common:cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
